import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

import { Colors } from "../../../configs";
import { formatDate } from "../../../util/common-functions";
import CustomModal from "../../common/custom-modal/custom-modal";
import { IBill } from "../../../redux/actions/pintagAction";

interface BillItem {
  id: string;
  service: string;
  description: string;
  quantity: number;
  status: string;
  unitPrice: number;
  totalPrice: number;
  createdAt: string;
}

interface ViewBillModalProps {
  open: boolean;
  onClose: () => void;
  billId: string;
  selectedId: number;
  bill?: IBill;
}

const parseCreatedAt = (input: string): Date | null => {
  if (!input) return null;

  const iso = new Date(input);
  if (!isNaN(iso.getTime())) return iso;

  try {
    const normalized = input.replace(/\./g, "").toUpperCase();
    const parts = normalized.split(/\s+/);
    if (parts.length < 3) return null;
    const [datePart, timePart, ampm] = parts;
    const datePieces = datePart.split("/");
    if (datePieces.length !== 3) return null;
    const [mm, dd, yyyy] = datePieces.map(Number);
    if (!Number.isFinite(mm) || !Number.isFinite(dd) || !Number.isFinite(yyyy))
      return null;
    const [hourStr, minuteStr] = timePart.split(":");
    let hours = Number(hourStr);
    const minutes = Number(minuteStr);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (ampm === "PM" && hours < 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    const d = new Date(yyyy, mm - 1, dd, hours, minutes);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
};

const formatCurrency = (amount?: number | null): string => {
  if (amount == null || Number.isNaN(Number(amount))) return `$ ${Number(0).toFixed(2)}`;
  return `$ ${Number(amount).toFixed(2)}`;
};

const normalizeToStrings = (arr: any): string[] => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((i) => {
      if (i == null) return null;
      if (typeof i === "string") return i;
      if (typeof i === "object") return i?.text ?? i?.name ?? i?.label ?? JSON.stringify(i);
      return String(i);
    })
    .filter(Boolean) as string[];
};

const ViewBillModal: React.FC<ViewBillModalProps> = ({
  open,
  onClose,
  billId,
  selectedId,
  bill,
}) => {
  const displayData: BillItem = useMemo(() => {
    const fallback: BillItem = {
      id: String(billId ?? ""),
      service: "",
      description: "-",
      quantity: 0,
      status: "-",
      unitPrice: 0,
      totalPrice: 0,
      createdAt: bill?.createdTime ?? "",
    };

    if (!bill) return fallback;

    let parsed: any = null;
    if (bill.billString) {
      try {
        parsed = JSON.parse(bill.billString);
      } catch (e) {
        console.error("Failed to parse billString:", e);
      }
    }

    const firstItem = parsed?.cartItems?.[0] ?? null;

    const service =
      firstItem?.serviceType?.categoryName ?? firstItem?.service ?? fallback.service;

    const baseParts = [
      firstItem?.garmentType?.name,
      firstItem?.materialType?.name,
      firstItem?.garmentStyle?.name,
    ].filter(Boolean);
    const baseDesc = baseParts.join(", ");

    const instructionItems = normalizeToStrings(
      firstItem?.instructions ??
        parsed?.cartItems?.[0]?.instructions ??
        parsed?.instructions ??
        []
    );
    const upchargeItems = normalizeToStrings(
      firstItem?.upcharges ?? parsed?.cartItems?.[0]?.upcharges ?? parsed?.upcharges ?? []
    );

    const instructionStr = instructionItems.length ? `+ Instructions- ${instructionItems.join(", ")}` : "";
    const upchargesStr = upchargeItems.length ? `+Upcharges- ${upchargeItems.join(", ")}` : "";

    const composedDescription =
      [baseDesc, instructionStr, upchargesStr].filter(Boolean).join(" ") || bill.description || fallback.description;

    const quantity = Number(firstItem?.qty ?? firstItem?.quantity ?? bill.totalItemCount ?? fallback.quantity);

    const computedUnitPrice = (() => {
      if (firstItem?.unitPrice != null) return Number(firstItem.unitPrice);
      const billAmt = typeof bill.billAmount === "number" ? bill.billAmount : null;
      if (billAmt != null && quantity > 0) return billAmt / Math.max(1, quantity);
      return fallback.unitPrice;
    })();
    const unitPrice = Number.isFinite(computedUnitPrice) ? computedUnitPrice : fallback.unitPrice;

    const totalPrice = Number(
      firstItem ? quantity * unitPrice : typeof bill.billAmount === "number" ? bill.billAmount : fallback.totalPrice
    );

    const status = firstItem?.finalized ? "Finalized" : bill.billStatus ?? fallback.status;
    const createdAt = bill.createdTime ?? parsed?.createdAt ?? fallback.createdAt;

    return {
      id: String(bill.billId ?? billId ?? fallback.id),
      service,
      description: composedDescription,
      quantity: Number.isFinite(quantity) ? quantity : fallback.quantity,
      status,
      unitPrice: Number.isFinite(unitPrice) ? unitPrice : fallback.unitPrice,
      totalPrice: Number.isFinite(totalPrice) ? totalPrice : fallback.totalPrice,
      createdAt,
    };
  }, [bill, billId]);

  const createdDateObj = parseCreatedAt(displayData.createdAt);
  const createdDateStr = createdDateObj
    ? formatDate(createdDateObj)
    : displayData.createdAt
    ? formatDate(displayData.createdAt)
    : "-";
  const createdTimeStr = createdDateObj
    ? createdDateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : displayData.createdAt
    ? String(displayData.createdAt).split(" ").slice(1).join(" ")
    : "";

  return (
    <CustomModal
      title=""
      openModal={open}
      onClose={onClose}
      width="52rem"
      height="65rem"
    >
      <Box
        sx={{
          paddingBottom:4,
          paddingLeft:1,
          paddingRight:1,
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: "0.71rem",
          boxShadow: "none",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            component="button"
            sx={{
              bgcolor: Colors.SECONDARY,
              color: Colors.WHITE,
              width: "8rem",
              height: "2.3rem",
              fontSize: "1.1rem",
              borderRadius: "0.5rem",
              padding: "0.25rem 0.75rem",
              border: "none",
              cursor: "pointer",
              marginTop: "-2rem",
              marginLeft: "0rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              display: "inline-block",
            }}
          >
            {`IN${displayData.id}`}
          </Box>
        </Box>

        <Box
          sx={{
            height: "100%",
            width: "100%",
            marginLeft: 0,
            marginTop: "1rem",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              position: "relative",
              pl: 5,
              pt: 2,
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                position: "relative",
                display: "flex",
                mb: 2,
                height: "100%",
                alignItems: "flex-start",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: "-6rem",
                  top: 0,
                  width: "5rem",
                  textAlign: "right",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "1rem",
                    color: Colors.MAIN_GRAY,
                    fontWeight: "bold",
                    lineHeight: 1.2,
                  }}
                >
                  {createdDateStr}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    color: Colors.MAIN_GRAY,
                    lineHeight: 1.2,
                  }}
                >
                  {createdTimeStr}
                </Typography>
              </Box>
              <Box
                sx={{
                  position: "absolute",
                  left: "-0.5rem",
                  top: "0.5rem",
                  width: "1rem",
                  height: "1rem",
                  bgcolor: Colors.SECONDARY,
                  borderRadius: "50%",
                  zIndex: 2,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  left: "-0.0714rem",
                  top: "1.5rem",
                  width: "0.1429rem",
                  height: "calc(100% - 1.5rem)",
                  bgcolor: Colors.SECONDARY,
                  zIndex: 1,
                }}
              />
              <Box
                sx={{
                  ml: 2,
                  border: `0.1429rem solid ${Colors.SECONDARY}`,
                  borderRadius: "0.8571rem",
                  p: 3,
                  bgcolor: Colors.WHITE,
                  width: "100%",
                  maxWidth: "40rem",
                  boxShadow: "0 0.1429rem 0.2857rem rgba(0,0,0,0.1)",
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  minHeight: "50rem",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.75,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      fontWeight: "bold",
                      minWidth: "8rem",
                      marginRight: 2,
                    }}
                  >
                    {displayData.service}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      flex: 1,
                    }}
                  >
                    {displayData.description}
                  </Typography>
                </Box>
              
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1.75,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      fontWeight: "bold",
                      minWidth: "8rem",
                      marginRight: 2,
                    }}
                  >
                    Service
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                    }}
                  >
                    {displayData.service}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1.75,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      fontWeight: "bold",
                      minWidth: "8rem",
                      marginRight: 2,
                    }}
                  >
                    Qty
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                    }}
                  >
                    {String(displayData.quantity).padStart(2, "0")}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1.75,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      fontWeight: "bold",
                      minWidth: "8rem",
                      marginRight: 2,
                    }}
                  >
                    Status
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                    }}
                  >
                    {displayData.status}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 1.75,
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      fontWeight: "bold",
                      minWidth: "8rem",
                      marginRight: 2,
                    }}
                  >
                    Unit Price
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                    }}
                  >
                    {formatCurrency(displayData.unitPrice)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                      fontWeight: "bold",
                      minWidth: "8rem",
                      marginRight: 2,
                    }}
                  >
                    Total Price
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "1.1429rem",
                      color: Colors.MAIN_GRAY,
                    }}
                  >
                    {formatCurrency(displayData.totalPrice)}
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default ViewBillModal;